import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { logError, logInfo } from '../utils/logger';

const TeamContext = createContext(null);

export const TeamProvider = ({ children }) => {
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({
    messages: 0,
    announcements: 0,
    files: 0,
    surveys: 0
  });

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    const unsubscribers = [];

    const setupListeners = async () => {
      try {
        // Create presence document for current user
        const userPresenceRef = doc(db, 'presence', auth.currentUser.uid);
        const presenceData = {
          userId: auth.currentUser.uid,
          email: auth.currentUser.email,
          name: auth.currentUser.displayName || auth.currentUser.email.split('@')[0],
          isAdmin: ['jfprince@soltec.ca', 'jfprince29@gmail.com', 'admin@oversizehub.net'].includes(auth.currentUser.email.toLowerCase()),
          companyId: auth.currentUser.uid,
          lastActive: serverTimestamp(),
          status: 'online',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(userPresenceRef, presenceData, { merge: true });

        // Listen for team members - include both company members and admins
        const teamRef = collection(db, 'userProfiles');
        const teamQuery = query(
          teamRef,
          where('companyId', 'in', [auth.currentUser.uid, 'admin'])
        );
        
        const teamUnsubscribe = onSnapshot(teamQuery, (snapshot) => {
          const members = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(member => member.id !== auth.currentUser.uid); // Exclude current user
          
          setTeamMembers(members);
          logInfo(`Loaded ${members.length} team members`);
        }, (error) => {
          logError('Error loading team members:', error);
          setTeamMembers([]);
        });
        unsubscribers.push(teamUnsubscribe);

        // Listen for presence - include both team members and admins
        const presenceRef = collection(db, 'presence');
        const presenceQuery = query(
          presenceRef,
          where('companyId', 'in', [auth.currentUser.uid, 'admin'])
        );
        
        const presenceUnsubscribe = onSnapshot(presenceQuery, (snapshot) => {
          const online = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(member => member.id !== auth.currentUser.uid); // Exclude current user

          setOnlineMembers(online);
          logInfo(`${online.length} team members online`);
        }, (error) => {
          logError('Error loading online members:', error);
          setOnlineMembers([]);
        });
        unsubscribers.push(presenceUnsubscribe);

        // Listen for announcements
        const announcementsRef = collection(db, 'team_announcements');
        const announcementsQuery = query(
          announcementsRef,
          where('companyId', '==', auth.currentUser.uid)
        );

        const announcementsUnsubscribe = onSnapshot(announcementsQuery, (snapshot) => {
          const unreadCount = snapshot.docs.reduce((count, doc) => {
            const data = doc.data();
            if (!data.read?.[auth.currentUser.uid]) {
              return count + 1;
            }
            return count;
          }, 0);

          setUnreadCounts(prev => ({
            ...prev,
            announcements: unreadCount
          }));
        });
        unsubscribers.push(announcementsUnsubscribe);

        setLoading(false);
      } catch (error) {
        logError('Failed to setup team listeners:', error);
        setLoading(false);
      }
    };

    setupListeners();

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  return (
    <TeamContext.Provider value={{ 
      onlineMembers, 
      teamMembers, 
      loading,
      unreadCounts
    }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};